import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFundManagers, getFunds, getInvestors, getIncidents, getNotifications } from '../api';
import axios from 'axios';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import {
  Briefcase, BarChart2, Users, AlertTriangle, CheckCircle,
  ArrowRight, ShieldCheck, Globe, ScrollText, Link2, TrendingUp,
} from 'lucide-react';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000' });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_COLORS = {
  ACTIVE:                  '#10B981',
  PENDING:                 '#94A3B8',
  KYC_IN_PROGRESS:         '#F59E0B',
  SUITABILITY_IN_PROGRESS: '#F59E0B',
  DOCUMENTS_PENDING:       '#F59E0B',
  SUSPENDED:               '#EF4444',
  REJECTED:                '#EF4444',
  GDPR_ERASED:             '#CBD5E1',
};

const IMPACT_COLORS = { HIGH: '#EF4444', MEDIUM: '#F59E0B', LOW: '#10B981' };

const SOURCE_COLORS = {
  ESMA:         '#3B82F6',
  EBA:          '#8B5CF6',
  LEGILUX:      '#10B981',
  CSSF_WEBSITE: '#F59E0B',
  CSSF_PORTAL:  '#F59E0B',
};

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, color, Icon, isMobile, theme }) => (
  <div style={{
    background:   theme.bgCard,
    borderRadius: 10,
    padding:      isMobile ? '16px' : '20px 24px',
    boxShadow:    theme.shadowSm,
    border:       `1px solid ${theme.border}`,
    display:      'flex',
    alignItems:   'center',
    gap:          16,
    minWidth:     0,
  }}>
    <div style={{
      width:          44,
      height:         44,
      borderRadius:   10,
      background:     color + '18',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      flexShrink:     0,
    }}>
      <Icon size={20} color={color} strokeWidth={1.75} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <p style={{
        margin: 0, fontSize: 11, fontWeight: 600,
        color: theme.textTertiary, letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}>
        {title}
      </p>
      <p style={{
        margin: '4px 0 0', fontSize: isMobile ? 24 : 28,
        fontWeight: 700, color: theme.textPrimary, lineHeight: 1,
      }}>
        {value}
      </p>
      {subtitle && (
        <p style={{ margin: '4px 0 0', fontSize: 11, color: theme.textSecondary }}>
          {subtitle}
        </p>
      )}
    </div>
  </div>
);

// ── Chart Card ───────────────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, children, theme }) => (
  <div style={{
    background:   theme.bgCard,
    borderRadius: 10,
    padding:      '20px 24px',
    boxShadow:    theme.shadowSm,
    border:       `1px solid ${theme.border}`,
  }}>
    <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
      {title}
    </p>
    {subtitle && (
      <p style={{ margin: '0 0 16px', fontSize: 12, color: theme.textTertiary }}>
        {subtitle}
      </p>
    )}
    {children}
  </div>
);

// ── Quick Link ───────────────────────────────────────────────────────────────
const QuickLink = ({ title, description, path, color, Icon, navigate, theme }) => (
  <div
    onClick={() => navigate(path)}
    style={{
      background:   theme.bgCard,
      borderRadius: 10,
      padding:      '16px 20px',
      boxShadow:    theme.shadowSm,
      border:       `1px solid ${theme.border}`,
      cursor:       'pointer',
      display:      'flex',
      alignItems:   'center',
      gap:          14,
      transition:   'all 0.15s',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.boxShadow = theme.shadowMd;
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.transform = 'translateY(-1px)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.boxShadow = theme.shadowSm;
      e.currentTarget.style.borderColor = theme.border;
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    <div style={{
      width: 36, height: 36, borderRadius: 8,
      background: color + '18',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={16} color={color} strokeWidth={1.75} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
        {title}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textSecondary }}>
        {description}
      </p>
    </div>
    <ArrowRight size={14} color={theme.textTertiary} />
  </div>
);

// ── Tooltip ──────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, theme }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:   theme.bgCard,
      border:       `1px solid ${theme.border}`,
      borderRadius: 8,
      padding:      '10px 14px',
      boxShadow:    theme.shadowMd,
      fontSize:     12,
    }}>
      {label && <p style={{ margin: '0 0 6px', fontWeight: 600, color: theme.textPrimary }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0', color: p.color || theme.textSecondary }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ── Main ─────────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate    = useNavigate();
  const isMobile    = useMobile();
  const { theme }   = useTheme();
  const user        = JSON.parse(localStorage.getItem('user') || '{}');

  const [stats,       setStats]       = useState({ fundManagers: 0, funds: 0, investors: 0, activeInvestors: 0, openIncidents: 0 });
  const [investors,   setInvestors]   = useState([]);
  const [documents,   setDocuments]   = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [fmRes, fundsRes, invRes, incRes, docsRes, impactRes] = await Promise.all([
          getFundManagers(),
          getFunds(),
          getInvestors(),
          getIncidents(),
          API.get('/regulatory/documents',        { params: { limit: 100 } }),
          API.get('/regulatory/impact-assessments', { params: { limit: 100 } }),
        ]);
        const inv = invRes.data;
        setInvestors(inv);
        setDocuments(docsRes.data.documents || []);
        setAssessments(impactRes.data.assessments || []);
        setStats({
          fundManagers:    fmRes.data.length,
          funds:           fundsRes.data.length,
          investors:       inv.length,
          activeInvestors: inv.filter(i => i.onboarding_status === 'ACTIVE').length,
          openIncidents:   incRes.data.incidents.filter(i => i.recovery_status === 'ONGOING').length,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const investorStatusData = Object.entries(
    investors.reduce((acc, i) => { acc[i.onboarding_status] = (acc[i.onboarding_status] || 0) + 1; return acc; }, {})
  ).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value, key: name }));

  const docsBySource = Object.entries(
    documents.reduce((acc, d) => { acc[d.source] = (acc[d.source] || 0) + 1; return acc; }, {})
  ).map(([source, count]) => ({ source, count }));

  const impactByLevel = ['HIGH', 'MEDIUM', 'LOW'].map(level => ({
    level, count: assessments.filter(a => a.impact_level === level).length,
  }));

  const docsByDate = Object.entries(
    documents.reduce((acc, d) => {
      const date = new Date(d.fetched_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {})
  ).slice(-7).map(([date, count]) => ({ date, count }));

  const axisStyle = { fontSize: 11, fill: theme.textTertiary };
  const gridStyle = { stroke: theme.border };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <p style={{ color: theme.textTertiary, fontSize: 14 }}>Loading…</p>
    </div>
  );

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.textPrimary }}>
          Dashboard
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          {user.full_name ? ` · Welcome back, ${user.full_name.split(' ')[0]}` : ''}
        </p>
      </div>

      {/* Stat cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: isMobile ? 12 : 16,
        marginBottom: 24,
      }}>
        <StatCard theme={theme} isMobile={isMobile} title="Fund Managers"  value={stats.fundManagers}  color="#3B82F6" Icon={Briefcase} />
        <StatCard theme={theme} isMobile={isMobile} title="Funds"          value={stats.funds}         color="#8B5CF6" Icon={BarChart2} />
        <StatCard theme={theme} isMobile={isMobile} title="Investors"      value={stats.investors}     color="#10B981" Icon={Users}
          subtitle={`${stats.activeInvestors} active`} />
        <StatCard theme={theme} isMobile={isMobile} title="DORA Incidents" value={stats.openIncidents}
          Icon={stats.openIncidents > 0 ? AlertTriangle : CheckCircle}
          color={stats.openIncidents > 0 ? '#EF4444' : '#10B981'}
          subtitle={stats.openIncidents > 0 ? 'Requires attention' : 'All clear'} />
      </div>

      {/* Charts row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <ChartCard theme={theme} title="Investor Status" subtitle="Breakdown by onboarding status">
          {investorStatusData.length === 0 ? (
            <p style={{ color: theme.textTertiary, fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No data</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={investorStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                  {investorStatusData.map(entry => <Cell key={entry.key} fill={STATUS_COLORS[entry.key] || '#94A3B8'} />)}
                </Pie>
                <Tooltip content={<CustomTooltip theme={theme} />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard theme={theme} title="Impact Assessments" subtitle="Regulatory impact by severity">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={impactByLevel} barSize={36}>
              <CartesianGrid strokeDasharray="3 3" {...gridStyle} vertical={false} />
              <XAxis dataKey="level" tick={axisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip theme={theme} />} />
              <Bar dataKey="count" name="Assessments" radius={[6, 6, 0, 0]}>
                {impactByLevel.map(entry => <Cell key={entry.level} fill={IMPACT_COLORS[entry.level]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <ChartCard theme={theme} title="Documents by Source" subtitle="Regulatory documents per body">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={docsBySource} barSize={32}>
              <CartesianGrid strokeDasharray="3 3" {...gridStyle} vertical={false} />
              <XAxis dataKey="source" tick={axisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip theme={theme} />} />
              <Bar dataKey="count" name="Documents" radius={[6, 6, 0, 0]}>
                {docsBySource.map(entry => <Cell key={entry.source} fill={SOURCE_COLORS[entry.source] || '#94A3B8'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard theme={theme} title="Documents Over Time" subtitle="Fetch activity — last 7 dates">
          {docsByDate.length === 0 ? (
            <p style={{ color: theme.textTertiary, fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No data</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={docsByDate}>
                <CartesianGrid strokeDasharray="3 3" {...gridStyle} vertical={false} />
                <XAxis dataKey="date" tick={axisStyle} axisLine={false} tickLine={false} />
                <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip theme={theme} />} />
                <Line type="monotone" dataKey="count" name="Documents" stroke="#3B82F6" strokeWidth={2}
                  dot={{ fill: '#3B82F6', r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Quick access */}
      <div>
        <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 600, color: theme.textSecondary, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Quick Access
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          <QuickLink theme={theme} navigate={navigate} title="Regulatory Monitor"  description="ESMA, EBA & CSSF updates"    path="/regulatory"         color="#3B82F6" Icon={Globe} />
          <QuickLink theme={theme} navigate={navigate} title="Impact Assessments"  description="Fund-level regulatory impact"  path="/impact-assessments" color="#8B5CF6" Icon={TrendingUp} />
          <QuickLink theme={theme} navigate={navigate} title="DORA Incidents"      description="ICT incident management"       path="/incidents"          color="#EF4444" Icon={AlertTriangle} />
          <QuickLink theme={theme} navigate={navigate} title="Investor Management" description="Onboarding and KYC status"     path="/investors"          color="#10B981" Icon={Users} />
          {['ADMIN','COMPLIANCE_OFFICER'].includes(user.role) && (
            <QuickLink theme={theme} navigate={navigate} title="Suitability"       description="MiFID II assessments"          path="/suitability"        color="#F59E0B" Icon={ShieldCheck} />
          )}
          {['ADMIN','COMPLIANCE_OFFICER'].includes(user.role) && (
            <QuickLink theme={theme} navigate={navigate} title="Audit Log"         description="Immutable compliance record"   path="/audit-log"          color="#6B7280" Icon={ScrollText} />
          )}
          {['ADMIN'].includes(user.role) && (
            <QuickLink theme={theme} navigate={navigate} title="Hash Chain"        description="Tamper detection verification" path="/hash-chain"         color="#1D4ED8" Icon={Link2} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;